import { switchMap, filter, tap } from "rxjs/operators";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Point from "ol/geom/Point";
import { STREAMTYPE } from "../../internal";
import Feature from "ol/Feature";
import Style from "ol/style/Style";
import Icon from "ol/style/Icon";
import { combineLatest } from "rxjs";
/**
 * @class GeolocationLayer
 * @classdesc Shows the current position of the Infra3D-Image in the map.
 */
export class GeolocationLayer {
    constructor(scene, navigator, locatorIcon) {
        this._layer = null;
        this._panorotation = 0;
        this._geolocation = null;
        this._orientation = 0;
        this._navigator = null;
        this._scene = null;
        this._locatorIcon = null;
        this._navigator = navigator;
        this._scene = scene;
        this._locatorIcon =
            locatorIcon ||
                "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAoCAYAAACM/rhtAAAABGdBTUEAALGPC/xhBQAAAAlwSFlzAAAOwQAADsEBuJFr7QAAABl0RVh0U29mdHdhcmUAcGFpbnQubmV0IDQuMC4xMK0KCsAAAAMMSURBVFhH7ZbNS1RRGMZ1dJxJyGJaWLh1oWhGUsoQDf0BLnTdov/AdmJEGjFhIOY6P1ByI0gb3YigCRFYaqKULlJRF0GroBbqfNyn5z33nQacy+jizAcxP3g483HO8z7z3nvO3DKbOI7TTrXq2+ICwBWGO6C+UAH9uHhgqAGGBEfhiX5cHDBQmIojHocomUzG+f62fl1Y2LSrDLQn3UN/PzA0ZF4y4Geq8JeaWV6ZRCsrQFUVUFMD7O6mLvUznVYYGEAubQInJ0A4DEheUSQCxGLmUsdisTs6Pb8wWA21L81zenvT4VIaMHumcJea3Rk0CVZXgWAwM2AoBOzsmCmc+0KX5Qd25C7l7trW1sxwKqepCTg9lS4mmPOBLs8tLBai9lgV6OsD/H6guRno6gK6u111dAANDYDP584hXLPFoVptcgcLvTQV19fhMIjD0XTyLOwctrfhdHYCW5LNhHytNrmBBe5RbB2R+yuZNC/PZXPTDFyapCJqZxf6y3/trqnkxeEhMDUFTE8DR0f6YSbcMN/oc0lt7UHT+5Tc7GmkmfPz3hulrQ1YXNSJaehxTN1SW3vQ20fjd24ZIuGGh4GKisxwKVVWwhl0T6MU7OAoh3K1tQsDPnXLkNnZ7OFSCgSAhQVdZDr4WO3sQm8ff/2BVgEaG70DeamlxSwTGPAjB/sdpLGcf79MFdmVXkGyaWPDLKXHPpWTTVJH/TFVJia8Q2ST7G5Cj9/UNbW1B03THZyc9A6RTemAP6jLamsPegdp/NNUkct1NsB5WlszS+nxlYNfbe1C8zdaxf2v9Qripfp6s0ygx7ja2YfmNym30kWPGT4wOHNzZgnXCu1qlxtYhzcgSfBPpaeHB0a5dzCRPM1Eo27HCY+pt2qTO9iBWuq7qSiFZ2aAurrMcHJZ5btUx3kXcp39zeEFC92gPmlhl6UlYGwMGBkBlpf/PYJxnnRuhWOtLs8PrF3NwlEW5oOfN/zumMNzjvYP5ovCANcTicQjhhinPjD0e46j1EPK/oFcokSJEiVK/E+Ulf0F2OLmmzUBSU0AAAAASUVORK5CYII=";
        const style = new Style({
            image: new Icon({
                src: this._locatorIcon,
                anchor: [0.5, 0.5],
                rotation: 0,
            }),
        });
        this._layer = new VectorLayer({
            zIndex: 300,
            style: (feature) => {
                style.getImage().setRotation(feature.getProperties()["orientation"]);
                return style;
            },
        });
        this._layer.setSource(new VectorSource());
        this._currentnodesubscription = combineLatest([
            this._scene.userinputapi.geoframechanged$,
            this._navigator.stateService.currentNode$.pipe(filter((node) => node.full)),
        ])
            .pipe(filter(([geoframe, node]) => {
            return node.key == geoframe.key;
        }), tap(([geoframe, node]) => {
            if (!(geoframe.getMeasureInfo().streamtype === STREAMTYPE.CALOTTE ||
                geoframe.getMeasureInfo().streamtype === STREAMTYPE.CUBEMAP)) {
                this._panorotation = 0;
            }
            else {
                this._panorotation = geoframe.parentGeoFrameCollection.getLatLon()[1];
            }
            this._orientation = geoframe.getHorizontalOrientation();
        }), switchMap(([geoframe, node]) => {
            return this._navigator.cameraService.cacheCameras$(node);
        }))
            .subscribe((allnodes) => {
            if (allnodes.length == 0)
                return;
            const eastings = allnodes.map((y) => y.latLon.lon);
            const average_easting = eastings.reduce(function (avg, value, _, { length }) {
                return avg + value / length;
            }, 0);
            const northings = allnodes.map((y) => y.latLon.lat);
            const average_northing = northings.reduce(function (avg, value, _, { length }) {
                return avg + value / length;
            }, 0);
            this._geolocation = new Point([average_easting, average_northing]);
            this._geolocation.transform(`EPSG:4326`, "EPSG:3857");
            this._drawSymbol();
        });
        this._panorotationevt = this._scene.userinputapi.panorotationchanged$.subscribe(({ lat: lat, lon: lon }) => {
            if (Math.abs(this._panorotation - lon) > 2) {
                this._panorotation = lon;
                this._drawSymbol();
            }
        });
    }
    get layer() {
        return this._layer;
    }
    _drawSymbol() {
        const features = this._layer.getSource().getFeatures();
        if (features.length == 0) {
            this._layer.getSource().addFeature(new Feature({
                geometry: this._geolocation,
                orientation: ((this._orientation + this._panorotation) * Math.PI) / 180,
            }));
        }
        else {
            const feature = features[0];
            feature.setGeometry(this._geolocation);
            feature.set("orientation", ((this._orientation + this._panorotation) * Math.PI) / 180);
        }
    }
    deactivate() {
        this._panorotationevt.unsubscribe();
        this._currentnodesubscription.unsubscribe();
    }
}
//# sourceMappingURL=GeolocationLayer.js.map